import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import useDismantlingBays from "../../data/useDismantlingBays";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import {
  DismantlingBayType,
  ICreateUpdateDismantlingBayRequest,
  IDismantlingBay,
} from "../../models/DismantlingBay";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

type FormValues = {
  name: string;
  capacity: number;
  easy: number;
  medium: number;
  hard: number;
  type: DismantlingBayType;
};

interface DismantlingBayModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dismantlingBay?: IDismantlingBay;
}

const typeOptions = [
  { id: DismantlingBayType.Bay, name: "Bay" },
  { id: DismantlingBayType.ProductionLine, name: "Production Line" },
];

export default function DismantlingBayModal({ open, setOpen, dismantlingBay }: DismantlingBayModalProps) {
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Dismantling Bay");
  const { createDismantlingBay, updateDismantlingBay, deleteDismantlingBay } = useDismantlingBays();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, setValue, watch } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    const payload: ICreateUpdateDismantlingBayRequest = {
      ...dismantlingBay,
      name: data.name,
      capacity: Number(data.capacity),
      easy: Number(data.easy),
      medium: Number(data.medium),
      hard: Number(data.hard),
      type: Number(data.type) as DismantlingBayType,
    };

    if (dismantlingBay !== undefined) {
      updateDismantlingBay.mutate(payload, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError,
      });
    } else {
      createDismantlingBay.mutate(payload, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError,
      });
    }
  };

  const handleDelete = () => {
    deleteDismantlingBay.mutate(dismantlingBay?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError,
    });
  };

  useEffect(() => {
    if (dismantlingBay !== undefined) {
      reset({
        name: dismantlingBay.name,
        capacity: dismantlingBay.capacity,
        easy: dismantlingBay.easy,
        medium: dismantlingBay.medium,
        hard: dismantlingBay.hard,
        type: dismantlingBay.type,
      });
    } else {
      reset({
        name: "",
        capacity: 0,
        easy: 0,
        medium: 0,
        hard: 0,
        type: DismantlingBayType.Bay,
      });
    }
  }, [dismantlingBay, open, reset]);

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title="Delete Dismantling Bay"
        message="Are you sure you want to delete this dismantling bay? This action cannot be undone."
        confirmButtonText="Delete"
        onConfirm={handleDelete}
        isLoading={deleteDismantlingBay.isLoading}
      />
      <Modal width="max-w-2xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{`${dismantlingBay ? "Edit" : "Add"} a Dismantling Bay`}</h1>
        <h1 className="text-sm text-gray-500">Configure the capacity and difficulty mix this bay or production line can handle.</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)}>
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">

              <TextInput label="Name" registerName="name" register={register} required={true} />

              <SelectInput
                label="Type"
                registerName="type"
                register={register}
                setValue={setValue}
                watch={watch}
                options={typeOptions}
                rounded={true}
              />

              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <NumberInput label="Capacity" registerName="capacity" register={register} min={0} step="1" />
              </div>

              <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
                <NumberInput label="Easy" registerName="easy" register={register} min={0} step="1" />
                <NumberInput label="Medium" registerName="medium" register={register} min={0} step="1" />
                <NumberInput label="Hard" registerName="hard" register={register} min={0} step="1" />
              </div>

              <ButtonInput
                label={`${dismantlingBay ? "Save Changes" : "Create"}`}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
                isLoading={createDismantlingBay.isLoading || updateDismantlingBay.isLoading}
              />
              {dismantlingBay !== undefined && (
                <ButtonInput
                  label="Delete"
                  isSubmit={false}
                  onClick={() => setDeleteOpen(true)}
                  classes="!bg-red-500 hover:!bg-red-600"
                />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
